import { useEffect } from "react";
import { createPortal } from "react-dom";
import "styles/offer-details-modal.css";

const OFFER_DETAILS = {
  wakeup: `# Wake-up – Energie für den Alltag

**2–3 Minuten. Im Sitzen oder Stehen. In Alltagskleidung. Für neue Energie genau dann, wenn du sie brauchst.**

Die Dr. Hoffmann Wake-up-Massage ist eine kompakte, speziell entwickelte Perkussionsmassage, die innerhalb von nur zwei bis drei Minuten ausgewählte Muskelgruppen des Oberkörpers, der Arme, des Rückens und der Gesäßregion gezielt anspricht.

Das Besondere dabei: Die einzelnen Massageelemente werden nicht beliebig miteinander kombiniert. Die Wake-up folgt einer **fest entwickelten Abfolge, einem definierten Rhythmus und einer genau abgestimmten Massagetechnik**. Dieses Zusammenspiel ist ein wesentlicher Bestandteil unseres Wake-up-Konzepts.

In die Massage werden unter anderem die **Unterarmmuskulatur mit ihren Flexoren und Extensoren** sowie die **Oberarmmuskulatur mit Bizeps und Trizeps** einbezogen.

Im Schulter- und Nackenbereich werden insbesondere Muskelgruppen wie der **Musculus trapezius**, der **Musculus levator scapulae** sowie die **Rhomboiden** angesprochen. Diese Bereiche werden im Alltag beispielsweise durch langes Sitzen, Bildschirmarbeit oder eine einseitige Körperhaltung häufig stark beansprucht.

Im oberen und mittleren Rücken umfasst die Wake-up unter anderem Bereiche des **Musculus trapezius**, der **Mm. rhomboidei** und des **Musculus latissimus dorsi**.

Auch der untere Rücken wird einbezogen. Hier liegen unter anderem der **Musculus erector spinae**, der **Musculus multifidus** sowie seitlich der **Musculus quadratus lumborum** – Muskelgruppen, die eine wichtige Rolle bei Haltung, Stabilisierung und Bewegung des Rumpfes spielen.

Ergänzend wird die Gesäßmuskulatur mit dem **Musculus gluteus maximus** und dem **Musculus gluteus medius** angesprochen. Diese kräftigen Muskelgruppen bilden eine wichtige funktionelle Verbindung zwischen Becken, Rumpf und Beinen.

Ein weiterer Bestandteil unseres Wake-up-Konzepts ist die gezielte sensorische Stimulation der Hände. Dort befinden sich zahlreiche sensible **Mechanorezeptoren**, unter anderem Meissner-, Merkel-, Ruffini- und Pacini-Rezeptoren. Sie reagieren auf Berührung, Druck, Bewegung und Vibration und liefern dem Nervensystem unmittelbar sensorische Informationen.

Alle diese Elemente werden innerhalb der Wake-up in einer **speziell entwickelten und aufeinander abgestimmten Abfolge** miteinander verbunden.

## Das 4-in-1-Prinzip

Die Wake-up verbindet vier zentrale Wirkungsbereiche in einer kurzen und gezielten Massage:

**1. Fördert die Durchblutung**

Die rhythmische Perkussionsmassage setzt gezielte mechanische Reize im Gewebe und unterstützt die lokale Durchblutung der beanspruchten Muskelbereiche.

**2. Löst Verspannungen**

Gezielt bearbeitete Muskelgruppen können sich lockern. Besonders beanspruchte Bereiche im Nacken, in den Schultern, im Rücken und in den Armen werden intensiv angesprochen.

**3. Beschleunigt die Regeneration**

Die Kombination aus rhythmischer Stimulation, Lockerung und gesteigerter Gewebeaktivität unterstützt den Körper dabei, sich nach Belastung schneller wieder frisch und leistungsfähig zu fühlen.

**4. Verbessert die Beweglichkeit**

Durch die gezielte Bearbeitung beanspruchter Muskelgruppen kann sich der Körper lockerer und beweglicher anfühlen. Bewegungen können wieder leichter und freier wahrgenommen werden.

## Das Ergebnis: Energie in wenigen Minuten

Die Wake-up wurde speziell für Situationen entwickelt, in denen wenig Zeit vorhanden ist und trotzdem ein deutlich wahrnehmbarer Impuls gewünscht wird.

Bereits nach wenigen Minuten kannst du dich **leichter, frischer, wacher und energiegeladener** fühlen.

Ob nach einer langen Autofahrt, einer Zug- oder Flugreise, nach stundenlangem Sitzen am Arbeitsplatz, vor einem Meeting, auf einer Veranstaltung oder einfach zwischendurch:

**Die Wake-up passt sich deinem Alltag an.**

Im Sitzen oder Stehen.  
In Alltagskleidung.  
Ohne aufwendige Vorbereitung.  
In nur zwei bis drei Minuten.

**Wake-up – neue Energie für deinen Alltag.**`,
  classic: `# Classic – Ganzkörperenergie für die Woche

**Intensive Ganzkörper-Perkussionsmassage. Ausschließlich im Liegen. Für mehr Beweglichkeit, Regeneration und neue Energie für die ganze Woche.**

Die Dr. Hoffmann Classic ist die konsequente Erweiterung unserer Wake-up-Massage zu einer intensiven Ganzkörpermassage.

Während sich die Wake-up auf ausgewählte Bereiche des Oberkörpers konzentriert und innerhalb weniger Minuten neue Energie für den Alltag geben soll, werden bei der Classic nahezu alle großen Muskelgruppen des Körpers einbezogen – vom Nacken und den Armen über den gesamten Rücken und die Gesäßmuskulatur bis hin zu Oberschenkeln und Unterschenkeln.

Die Classic folgt dabei einer speziell entwickelten Abfolge, einem definierten Rhythmus und einer aufeinander abgestimmten Perkussionstechnik. Die genaue Reihenfolge der einzelnen Massageelemente ist Bestandteil unseres Dr.-Hoffmann-Konzepts.

## Welche Bereiche werden bei der Classic angesprochen?

Im Bereich der Arme werden die Flexoren und Extensoren der Unterarme sowie Bizeps und Trizeps in die Massage einbezogen.

Im Schulter- und Nackenbereich werden unter anderem der Musculus trapezius, der Musculus levator scapulae und die Rhomboiden angesprochen.

Im oberen, mittleren und unteren Rücken umfasst die Classic unter anderem den Musculus latissimus dorsi, den Musculus erector spinae, den Musculus multifidus sowie den seitlich gelegenen Musculus quadratus lumborum.

Auch die Gesäßmuskulatur mit Musculus gluteus maximus, medius und minimus wird intensiv einbezogen. Sie übernimmt wichtige Aufgaben bei Stabilität, Haltung und der Kraftübertragung zwischen Ober- und Unterkörper.

Der entscheidende Unterschied zur Wake-up liegt jedoch in der zusätzlichen Bearbeitung der gesamten Beinmuskulatur.

An der Vorderseite des Oberschenkels befindet sich der kräftige Musculus quadriceps femoris, der insbesondere für die Streckung des Kniegelenks verantwortlich ist.

Auf der Rückseite werden die sogenannten ischiocruralen Muskeln – häufig als Hamstrings bezeichnet – angesprochen. Dazu gehören der Musculus biceps femoris, der Musculus semitendinosus und der Musculus semimembranosus.

Auch die Adduktorengruppe an der Innenseite des Oberschenkels wird berücksichtigt. Diese Muskelgruppe stabilisiert unter anderem das Bein und führt es zur Körpermitte.

Im Bereich des Unterschenkels werden insbesondere die kräftigen Wadenmuskeln Musculus gastrocnemius und Musculus soleus sowie weitere beanspruchte Muskelgruppen des Unterschenkels einbezogen.

Gerade Beine, Gesäß und unterer Rücken sind im Alltag häufig hohen Belastungen ausgesetzt – durch langes Sitzen, langes Stehen, Sport oder einseitige Bewegungsmuster.

Die Classic ermöglicht es, diese Bereiche deutlich umfassender und intensiver zu bearbeiten als bei einer kurzen Wake-up-Massage.

## Das 4-in-1-Prinzip – deutlich intensiver

Die Classic basiert auf denselben vier zentralen Wirkungsbereichen wie unsere Wake-up – durch die Ganzkörperbehandlung jedoch wesentlich umfangreicher.

**1. Fördert die Durchblutung**

Die intensive rhythmische Perkussion setzt mechanische Reize in großen Bereichen der Muskulatur. Dadurch kann die lokale Durchblutung der beanspruchten Bereiche unterstützt und deutlich wahrnehmbar angeregt werden.

**2. Löst Verspannungen**

Durch die intensive Bearbeitung von Nacken, Schultern, Rücken, Gesäß und Beinen können verspannte und verhärtete Muskelbereiche gezielt gelockert werden.

Auch fest oder unbeweglich empfundene Bereiche des Muskel- und Fasziengewebes – besonders häufig in Beinen und Gesäß – können intensiv bearbeitet werden.

**3. Beschleunigt die Regeneration**

Durch die umfassende Stimulation der Muskulatur und die intensive Lockerung beanspruchter Körperbereiche ist die Classic darauf ausgerichtet, die körperliche Regeneration nach Belastungen zu unterstützen und ein länger anhaltendes Gefühl von Frische und Leistungsfähigkeit zu fördern.

**4. Verbessert die Beweglichkeit**

Werden große Muskelgruppen des gesamten Körpers intensiv angesprochen, kann sich der Körper anschließend lockerer, freier und beweglicher anfühlen.

Gerade die zusätzliche Bearbeitung von Oberschenkeln, Waden, Gesäß und unterem Rücken macht den Unterschied zur kurzen Wake-up besonders deutlich.

## Warum ausschließlich im Liegen?

Die Classic ist eine intensive Ganzkörpermassage und kann deshalb ausschließlich im Liegen durchgeführt werden.

Nur in dieser Position kann sich der Körper ausreichend entspannen und können die unterschiedlichen Muskelgruppen von Oberkörper, Rücken, Gesäß und Beinen systematisch und kontrolliert erreicht werden.

Die Classic ist dabei bewusst keine reine Entspannungsmassage.

Sie ist kräftiger, intensiver und darauf ausgerichtet, einen deutlich wahrnehmbaren körperlichen Impuls zu setzen.

Deshalb empfehlen wir unmittelbar nach der Massage eine kurze Ruhe- und Erholungsphase von etwa zwei bis drei Minuten.

Der Körper bekommt so die Möglichkeit, nach der intensiven Stimulation und der gesteigerten Aktivität der Muskulatur kurz zur Ruhe zu kommen.

## Das Ergebnis: Ganzkörperenergie für die Woche

Die Classic wurde für Menschen entwickelt, denen ein kurzer Energieimpuls nicht genügt.

Hier wird der Körper umfassend behandelt.

Das Ziel ist ein Gefühl von:

mehr Leichtigkeit, mehr Beweglichkeit, gelockerter Muskulatur, unterstützter Regeneration und neuer Energie für die ganze Woche.

Die Classic eignet sich deshalb besonders als regelmäßig wiederkehrende Wellnessmassage – beispielsweise einmal pro Woche.

**Classic – Ganzkörperenergie für die Woche.**

## Classic – die intensive Ganzkörpermassage

Die Classic wird grundsätzlich im Liegen durchgeführt und ist auf eine einzelne intensive Ganzkörpermassage ausgerichtet.

Sie eignet sich besonders für alle, die regelmäßig eine umfassende Massage genießen und sämtliche großen Muskelgruppen des Körpers einbeziehen möchten.

Die Classic findet in unserem Studio statt.

Für die Durchführung werden die vorhandene Massageausstattung sowie die erforderlichen Hygiene- und Reinigungsmaterialien genutzt. Wird eine Classic in einer Umgebung durchgeführt, in der die notwendige Ausstattung nicht Bestandteil des Angebots ist, müssen die erforderliche Liege sowie das benötigte Massage- und Hygienezubehör entsprechend bereitgestellt werden.

Der Schwerpunkt liegt auf unserem fest entwickelten Classic-Ablauf und der umfassenden Bearbeitung der großen Muskelgruppen.

**Classic – der intensive Energieimpuls für den gesamten Körper.**`,
  intense: `# Intense – neue Lebensenergie für dich

**Massage, Bewegung, Ernährung und persönliches Coaching – individuell auf dich und deine Ziele abgestimmt.**

Die Dr. Hoffmann Intense geht deutlich über eine klassische Massage hinaus.

Sie verbindet die intensive Ganzkörper-Perkussionsmassage der Classic mit individueller Körperarbeit, Personal Training, Stretching, einem persönlichen Trainingsprogramm für zu Hause und Ernährungsberatung.

Dabei gibt es kein starres Programm, das für jeden Menschen gleich aussieht.

Die Intense beginnt mit dir.

## Am Anfang steht dein persönliches Ziel

Zu Beginn führen wir ein ausführliches persönliches Gespräch.

Dabei geht es zunächst um die Frage: **Was möchtest du erreichen?**

Das können zum Beispiel sein:

- mehr Beweglichkeit und körperliche Leistungsfähigkeit,
- weniger muskuläre Beschwerden und Verspannungsgefühle,
- bessere Regeneration,
- mehr Energie im Alltag,
- eine Verbesserung der körperlichen Fitness,
- eine Veränderung von Bewegungs- oder Ernährungsgewohnheiten,
- oder ganz allgemein das Ziel, sich körperlich wieder wohler und leistungsfähiger zu fühlen.

Die persönlichen Ziele und die besprochenen Maßnahmen werden dokumentiert und bilden die Grundlage für die weitere Begleitung.

Denn Intense bedeutet nicht, einfach nur eine Massage durchzuführen.

Wir betrachten den Menschen ganzheitlich und richten die einzelnen Maßnahmen auf ein gemeinsames Ziel aus.

## Das Intense-4-in-1-Konzept

**1. Zielklärung und persönliche Begleitung**

Jede Intense beginnt mit einer individuellen Betrachtung deiner persönlichen Situation. Gemeinsam besprechen wir deine Ziele, deine aktuellen Belastungen, deine Bewegungsgewohnheiten und die Bereiche, in denen du Veränderungen erreichen möchtest. Dieses Ziel behalten wir während der gesamten Begleitung im Blick. Die Maßnahmen werden entsprechend angepasst und weiterentwickelt.

**2. Ernährung**

Körperliche Leistungsfähigkeit und Wohlbefinden hängen nicht allein von Bewegung und Massage ab. Deshalb gehört zur Intense auch das Thema Ernährung. Gemeinsam betrachten wir, welche Ernährungsgewohnheiten zu deinem persönlichen Ziel passen und welche Veränderungen sich sinnvoll in deinen Alltag integrieren lassen. Dabei geht es nicht um kurzfristige Extremprogramme, sondern um eine Ernährung, die sich dauerhaft mit deinem persönlichen Alltag verbinden lässt.

**3. Bewegung, Personal Training und Training für zu Hause**

Die Intense beinhaltet ein auf deine Möglichkeiten abgestimmtes Personal Training. Dabei betrachten wir insbesondere Beweglichkeit, Stabilität, Kraft und die Muskelgruppen, die für deine persönlichen Ziele besonders wichtig sind.

Zusätzlich erhältst du Übungen, die du selbstständig zwischen den gemeinsamen Terminen durchführen kannst. So endet das Training nicht mit der jeweiligen Intense-Einheit. Die entscheidende Veränderung findet auch zwischen den Terminen statt. Das persönliche Home-Training-Programm soll dir ermöglichen, die gemeinsam erarbeiteten Impulse regelmäßig in deinen Alltag zu integrieren.

**4. Individuelle Körperarbeit**

Der vierte Bestandteil ist die intensive und individuell angepasste Körperarbeit. Je nach persönlichem Bedarf können unterschiedliche Methoden miteinander kombiniert werden: Perkussionsmassage, Ganzkörpermassage, gezieltes Stretching, manuelle Massage und punktuelle Ölmassage.

Die Perkussionsmassage setzt gezielte rhythmische Reize in der Muskulatur und kann die lokale Durchblutung unterstützen, verspannte Muskelbereiche lockern und die Beweglichkeit fördern.

Stretching ergänzt die Massage durch gezielte Bewegungs- und Dehnreize. Wo es sinnvoll ist, können einzelne Muskel- oder Gewebebereiche zusätzlich manuell und mit Öl bearbeitet werden.

**Das bedeutet: Nicht du musst dich an unsere Massage anpassen – die Intense wird an dich angepasst.**

## Das Ziel: nachhaltige Veränderung statt kurzer Impuls

Die Wake-up gibt dir einen unmittelbaren Energieimpuls für den Alltag. Die Classic bietet intensive Ganzkörperenergie für die Woche. Die Intense geht einen Schritt weiter.

Sie verbindet Massage, Bewegung, Ernährung und persönliche Begleitung mit dem Ziel, langfristig neue Routinen aufzubauen und körperliche Veränderungen zu unterstützen.

Deshalb verstehen wir die Intense als kontinuierliche Begleitung.

## Der Einstieg: vier Wochen gemeinsam starten

In der Regel beginnen wir mit vier Intense-Einheiten im wöchentlichen Rhythmus. Diese erste Phase dient dazu, den Körper kennenzulernen, individuelle Schwerpunkte festzulegen, Übungen einzuarbeiten und erste Veränderungen im Alltag umzusetzen.

Nach diesen vier Einheiten kann die weitere Begleitung auf eine Intense-Einheit pro Monat reduziert werden.

Zwischen den Terminen setzt du dein persönliches Bewegungs- und Trainingsprogramm eigenständig fort.

So entsteht aus einzelnen Terminen ein langfristiges Konzept.

**Intense – neue Lebensenergie für dich.**`,
  "wakeup-plus-business": `# Wake-up Plus – das Plus für mehr Zeit und Individualität

Die **Wake-up Plus** basiert auf unserem bewährten Wake-up-Prinzip und erweitert dieses um einen entscheidenden Vorteil:

**mehr Zeit für dich oder dein Team und mehr Raum für individuelle Wünsche.**

Bei der Wake-up Plus sind wir **bis zu 30 Minuten vor Ort** und können in diesem Zeitraum bis zu **zehn Personen** mit der Wake-up-Massage betreuen.

Damit eignet sich Wake-up Plus besonders für Unternehmen, Teams, Meetings, Veranstaltungen, Schulungen, Empfänge, Neueröffnungen, Weihnachtsfeiern und viele weitere Anlässe.

Doch das **Plus** bedeutet noch mehr.

Wenn bei einzelnen Teilnehmern bestimmte Bereiche besondere Aufmerksamkeit benötigen, können wir innerhalb des verfügbaren Zeitrahmens gezielter darauf eingehen.

Das können beispielsweise ausgeprägte muskuläre Verspannungen, besonders beanspruchte Muskelgruppen, verhärtete Gewebebereiche oder subjektiv als eingeschränkt empfundene Körperregionen sein. Auch Bereiche, in denen sich Muskeln und Faszien besonders fest oder unbeweglich anfühlen, können intensiver berücksichtigt werden.

Dabei bleibt die Wake-up Plus eine **Wellnessmassage**. Sie ersetzt keine medizinische Untersuchung, Diagnose oder therapeutische Behandlung.

## Aus 4-in-1 wird 4-in-1 Plus

Das bewährte Wake-up-Konzept steht für:

**Durchblutung fördern. Verspannungen lösen. Regeneration beschleunigen. Beweglichkeit verbessern.**

Das **Plus** bedeutet:

**mehr Zeit, individuelle Aufmerksamkeit und die Möglichkeit, auf persönliche Wünsche und besonders beanspruchte Bereiche gezielter einzugehen.**

Und für Unternehmen und Veranstaltungen bedeutet das zusätzlich:

**bis zu zehn Personen innerhalb eines Termins.**

Im Sitzen oder Stehen.  
In Alltagskleidung.  
Direkt vor Ort.  
Bis zu 30 Minuten.

**Wake-up Plus – neue Energie für dich, dein Team und deinen Alltag.**`,
  "classic-plus-business": `# Classic Plus – mehr Service, mehr Individualität

Die **Classic Plus** basiert vollständig auf unserem Classic-Konzept:

Ganzkörpermassage im Liegen, intensive Perkussion und die Kombination aus:

**Durchblutung fördern. Verspannungen lösen. Regeneration beschleunigen. Beweglichkeit verbessern.**

Das **Plus** beginnt dort, wo mehr Flexibilität und mehr individuelle Betreuung gefragt sind.

Bei der Classic Plus bringen wir die für den Einsatz benötigte **Massageausstattung sowie das erforderliche Zubehör und die Hygieneausstattung selbst mit**.

Das ist insbesondere dann sinnvoll, wenn wir vor Ort nicht nur eine einzelne Massage durchführen, sondern **mehrere Personen nacheinander betreuen**.

So wird die Classic Plus zur idealen Lösung für Unternehmen, Teams und Gruppen, die die intensive Ganzkörpermassage direkt vor Ort nutzen möchten.

## Das Plus: individueller auf den Menschen eingehen

Bei der Classic Plus haben wir zusätzlich die Möglichkeit, innerhalb des jeweiligen Massageablaufs stärker auf persönliche Bedürfnisse einzugehen.

Hat jemand beispielsweise besonders beanspruchte Schultern, einen stark belasteten unteren Rücken, verspannte Oberschenkel oder sehr feste Bereiche in der Waden- oder Gesäßmuskulatur, können diese Regionen intensiver berücksichtigt werden.

Auch Bereiche, in denen sich das Binde- und Fasziengewebe besonders fest, unbeweglich oder „verklebt“ anfühlt, können gezielter und länger bearbeitet werden.

Dabei bleibt die Classic Plus eine **Wellnessmassage** und ersetzt keine medizinische Untersuchung, Diagnose oder physiotherapeutische Behandlung.

## Aus 4-in-1 wird Classic Plus

Die Classic verbindet:

**1. Durchblutung fördern  2. Verspannungen lösen  3. Regeneration beschleunigen  4. Beweglichkeit verbessern**

Das **Plus** bedeutet:

**komplette Ausstattung vor Ort, mehrere Massagen hintereinander, mehr Flexibilität und mehr individuelle Aufmerksamkeit für besonders beanspruchte Körperbereiche.**

Ausschließlich im Liegen.  
In Alltagskleidung.  
Als intensive Ganzkörpermassage.  
Mit kurzer Erholungsphase im Anschluss.

**Classic Plus – Ganzkörperenergie für die Woche, individuell und direkt vor Ort.**`,
  "intense-plus-business": `# Intense Plus – neue Lebensenergie für dich und dein Team

Die **Intense Plus** überträgt das persönliche Intense-Konzept auf **Unternehmen, Teams, Gruppen und Veranstaltungen**.

Dabei verbinden wir die individuelle Betreuung einzelner Teilnehmer mit gemeinsamen Trainings- und Bewegungseinheiten.

Das macht die Intense Plus besonders interessant für Unternehmen, die ihren Mitarbeitern nicht nur eine einzelne Massage anbieten möchten, sondern ein langfristiges Konzept für **Bewegung, Regeneration, Aktivierung und gesundheitsorientierte Alltagsroutinen** schaffen wollen.

## Personal Coaching auch für größere Gruppen

Während das Personal Training bei der klassischen Intense auf eine einzelne Person ausgerichtet ist, können wir bei der **Intense Plus auch größere Gruppen gemeinsam betreuen**.

Das kann zum Beispiel ein gemeinsames Mobilitäts-Training, Stretching, Aktivierungstraining, Bewegungstraining oder Personal Training in der Gruppe sein.

Dabei können einzelne Übungen so aufgebaut werden, dass sie anschließend auch selbstständig am Arbeitsplatz oder zu Hause durchgeführt werden können.

## Persönliches Onboarding für jeden Teilnehmer

Gerade bei längerfristigen Unternehmensprogrammen beginnen wir nach Möglichkeit mit einem persönlichen **Onboarding**.

Dabei können mit den einzelnen Teilnehmern individuelle Ziele und körperliche Schwerpunkte besprochen werden.

So erhält jeder Teilnehmer persönliche Hinweise und Übungen, obwohl das anschließende Programm teilweise gemeinsam in der Gruppe stattfindet.

Das ist insbesondere dann sinnvoll, wenn beispielsweise regelmäßige monatliche Massagen, Trainings oder Gesundheitstage im Unternehmen geplant sind.

Die Teilnehmer können dadurch bereits zwischen den gemeinsamen Terminen selbst aktiv werden.

## Intense Plus bei Veranstaltungen und Events

Die Intense Plus ist nicht auf unser Studio oder Hamburg beschränkt.

**Wir bieten dieses Konzept weltweit an.**

Damit eignet sich Intense Plus auch für Unternehmensveranstaltungen, Tagungen, Kongresse, mehrtägige Events, Messen, Firmenreisen, Management-Veranstaltungen, Team-Events und internationale Veranstaltungen.

Je nach Veranstaltung entwickeln wir aus Massage, Aktivierung, Stretching, Personal Training und Coaching ein Konzept, das zum jeweiligen Ablauf und zur Teilnehmerzahl passt.

Dabei kann Intense Plus sowohl als einzelner Programmpunkt als auch als begleitendes Angebot über mehrere Stunden oder mehrere Veranstaltungstage eingesetzt werden.

## Unsere Kalkulationsbasis für Intense Plus

Für Intense-Plus-Einsätze bei Unternehmen, Gruppen und Veranstaltungen gilt als grundsätzliche Kalkulationsbasis:

**125,00 € netto pro eingesetzter Stunde zzgl. anfallender Spesen und Reisekosten.**

Auf dieser Grundlage wird der jeweilige Einsatz entsprechend Dauer, Teilnehmerzahl, Reiseaufwand, benötigter Ausstattung und Veranstaltungsformat kalkuliert.

Damit lassen sich sowohl kleinere Teamtermine als auch umfangreiche nationale und internationale Veranstaltungen individuell planen.

## Das Plus der Intense Plus

Die Intense verbindet persönliche Zielklärung, Ernährung, Personal Training und Home-Training sowie individuell abgestimmte Massage- und Körperarbeit.

Die **Intense Plus** erweitert dieses Konzept um Training für größere Gruppen, individuelles Teilnehmer-Onboarding, Team- und Unternehmensprogramme, Veranstaltungen und Events sowie weltweite Einsatzmöglichkeiten.

Damit wird aus einer persönlichen Begleitung ein Konzept für ganze Teams.

**Intense – neue Lebensenergie für dich.**

**Intense Plus – neue Lebensenergie für dich und dein Team.**

*Die Angebote von Dr. Hoffmann sind Wellness-, Bewegungs- und Coachingangebote. Sie ersetzen keine ärztliche Diagnose, medizinische Behandlung oder physiotherapeutische Therapie. Bei akuten oder ungeklärten Schmerzen sollte vor der Teilnahme eine medizinische Abklärung erfolgen.*`,
};

const escapeHtml = (value) =>
  value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const inlineMarkdown = (value) =>
  escapeHtml(value).replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>").replace(/\*(.+?)\*/g, "<em>$1</em>");

const renderMarkdown = (markdown) => {
  const blocks = markdown.trim().split(/\n\s*\n/);
  return blocks
    .map((block) => {
      const lines = block.split("\n");
      const heading = lines[0].match(/^(#{1,2})\s+(.+)$/);
      if (heading) {
        const level = heading[1].length;
        return `<h${level}>${inlineMarkdown(heading[2])}</h${level}>`;
      }
      if (lines.every((line) => /^-\s+/.test(line))) {
        return `<ul>${lines.map((line) => `<li>${inlineMarkdown(line.slice(2))}</li>`).join("")}</ul>`;
      }
      return `<p>${lines.map(inlineMarkdown).join(" ")}</p>`;
    })
    .join("");
};

const OfferDetailsModal = ({ offerId, onClose }) => {
  const content = OFFER_DETAILS[offerId];

  useEffect(() => {
    if (!content) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [content, onClose]);

  if (!content) return null;
  const title = content.match(/^#\s+(.+)$/m)?.[1] || "Mehr erfahren";

  return createPortal(
    <div className="offer-details-overlay" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <section className="offer-details-dialog" role="dialog" aria-modal="true" aria-label={title}>
        <button className="offer-details-close" type="button" onClick={onClose} aria-label="Popup schließen">×</button>
        <div className="offer-details-content" dangerouslySetInnerHTML={{ __html: renderMarkdown(content) }} />
      </section>
    </div>,
    document.body
  );
};

export default OfferDetailsModal;
